import { AnimatePresence, motion } from 'motion/react';
import { type ComponentType, type ReactElement, useMemo } from 'react';
import { atomFamily, useRecoilState } from 'recoil';

import * as api from '@gitbook/api-client';
import { createSiteEvent } from '@gitbook/app-analytics';
import { logEvent } from '@gitbook/app-analytics/client';
import { Button, Card, CardBody } from '@gitbook/spine-react';

import { AdaptiveContentFeatureIntro } from '../../../Components';
import { useUpgradeSite } from '../../../state';
import { persistRecoilEffect } from '../../../utils';
import { SiteStructureIntroCard } from './SiteStructureIntroCard';

const ignoredSiteFeatureCardKeys = atomFamily<Set<string>, string>({
    key: 'ignoredSiteFeatureCardKeys',
    default: new Set<string>(),
    effects: [
        persistRecoilEffect({
            serializer: {
                serialize: (value) => JSON.stringify(Array.from(value)),
                deserialize: (value) => new Set(JSON.parse(value)),
            },
        }),
    ],
});

interface FeatureCardProps {
    site: api.Site;
    organizationId: string;
    onDismiss: () => void;
    logAction: () => void;
}

const FEATURE_CARDS: {
    key: string;
    component: ComponentType<FeatureCardProps>;
    isEnabled: (props: {
        site: api.Site;
        structure: api.SiteStructure;
        organizationId: string;
    }) => boolean;
}[] = [
    {
        key: 'sections',
        component: SiteOverviewSectionsFeatureCard,
        isEnabled: () => true,
    },
    {
        key: 'adaptive-content',
        component: SiteAdaptiveContentFeatureCard,
        isEnabled: ({ site }) => {
            return !site.adaptiveContent?.enabled;
        },
    },
];

export function SiteRandomFeatureCard(props: {
    site: api.Site;
    siteStructure: api.SiteStructure;
    organizationId: string;
}) {
    const { site, siteStructure, organizationId } = props;
    const [ignoredKeys, setIgnoredKeys] = useRecoilState(ignoredSiteFeatureCardKeys(site.id));
    const elements = useMemo(() => {
        return FEATURE_CARDS.map(({ key, component: FeatureCard, isEnabled }) => {
            if (ignoredKeys.has(key)) {
                return null;
            }
            if (!isEnabled({ site, structure: siteStructure, organizationId })) {
                return null;
            }
            return (
                <FeatureCard
                    key={key}
                    site={site}
                    organizationId={organizationId}
                    onDismiss={() => {
                        setIgnoredKeys(new Set(ignoredKeys).add(key));
                        logEvent(
                            createSiteEvent(site, {
                                type: 'site_feature_card_dismiss',
                                feature: key,
                            })
                        );
                    }}
                    logAction={() => {
                        logEvent(
                            createSiteEvent(site, {
                                type: 'site_feature_card_action',
                                feature: key,
                            })
                        );
                    }}
                />
            );
        }).filter((element) => element !== null);
    }, [site, siteStructure, organizationId, setIgnoredKeys, ignoredKeys]);

    return <SingleElement elements={elements} />;
}

function SingleElement(props: { elements: ReactElement[] }) {
    const { elements } = props;
    const randomIndex = useMemo(() => Math.floor(Math.random() * elements.length), [elements]);
    return (
        <AnimatePresence>
            {elements[randomIndex] ? (
                <motion.div exit={{ opacity: 0 }} animate={{ opacity: 1 }} initial={{ opacity: 0 }}>
                    {elements[randomIndex]}
                </motion.div>
            ) : null}
        </AnimatePresence>
    );
}

function SiteOverviewSectionsFeatureCard(props: FeatureCardProps) {
    const { site, organizationId, onDismiss, logAction } = props;
    const upgrade = useUpgradeSite(site, api.SiteFeatureKey.SitesSections);
    return (
        <SiteStructureIntroCard
            onDismiss={onDismiss}
            upgrade={upgrade}
            action={
                <Button
                    href={{
                        screen: {
                            type: 'site',
                            site: site.id,
                            organization: organizationId,
                            tab: { type: 'settings', section: 'structure' },
                        },
                    }}
                    onPress={() => logAction()}
                    label="Manage structure"
                />
            }
        />
    );
}

function SiteAdaptiveContentFeatureCard(props: FeatureCardProps) {
    const { logAction, onDismiss, site } = props;

    return (
        <Card onDismiss={onDismiss}>
            <CardBody>
                <AdaptiveContentFeatureIntro
                    onFeatureEnabled={() => {
                        logAction();

                        logEvent(
                            createSiteEvent(site, {
                                type: 'adaptive_content_enable',
                                source: 'feature-card',
                            })
                        );
                    }}
                />
            </CardBody>
        </Card>
    );
}
